// API service for Qualia Dashboard
import permissionManager from '../utils/permissionManager';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";
const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL || 'ws://127.0.0.1:8001';

// Token management helpers
const getAccessToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('access_token', accessToken);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// HTTP request helper with automatic token refresh
const makeAuthenticatedRequest = async (url, options = {}) => {
  const accessToken = getAccessToken();
  
  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, requestOptions);
    
    // If token expired, try to refresh
    if (response.status === 401 && accessToken) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the request with new token
        requestOptions.headers['Authorization'] = `Bearer ${getAccessToken()}`;
        return await fetch(url, requestOptions);
      } else {
        // Refresh failed, redirect to login
        clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
    }
    
    return response;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
};

// Refresh access token
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh: refreshToken })
    });

    if (response.ok) {
      const data = await response.json();
      setTokens(data.access, data.refresh);
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

const apiService = {
  // Authentication methods
  login: async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error('Login failed');
        error.response = { data: errorData };
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await fetch(`${API_BASE_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`
          },
          body: JSON.stringify({ refresh: refreshToken })
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
    }
  },

  getUserProfile: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/auth/profile/`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = getAccessToken();
    if (!token) return false;
    
    try {
      // Check if token is expired (basic check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        console.log({user})
        // Initialize permission manager if user data exists
        // if (user && !permissionManager.isInitialized()) {
        //   permissionManager.setUser(user);
        // }
        return user;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Function to get estimated closing data
  getEstimatedClosingData: async (statusFilter = null, startDate = null, endDate = null) => {
    try {
      // Use provided dates or calculate default range: 1 month ago to current date
      let start_date, end_date;
      
      if (startDate && endDate) {
        start_date = startDate;
        end_date = endDate;
      } else {
        const currentDate = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(currentDate.getMonth() - 1);
        
        // Format dates as YYYY-MM-DD
        const formatDate = (date) => {
          return date.toISOString().split('T')[0];
        };
        
        start_date = formatDate(oneMonthAgo);
        end_date = formatDate(currentDate);
      }
      
      const requestBody = {
        start_date,
        end_date
      };
      
      // Add status filter if provided
      if (statusFilter && statusFilter !== 'all') {
        requestBody.status_filter = statusFilter;
      }
      
      const response = await fetch(`${API_BASE_URL}/automation/estimated-closing/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Estimated Closing API Response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching estimated closing data:', error);
      throw error;
    }
  },

  // Function to get all estimated closings with pagination
  getAllEstimatedClosings: async (page = 1, pageSize = 20, statusFilter = null, orderBy = '-estimated_closing') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        order_by: orderBy
      });
      
      // Add status filter if provided
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      
      const response = await fetch(`${API_BASE_URL}/automation/estimated-closing-list/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Estimated Closing List API Response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching estimated closing list:', error);
      throw error;
    }
  },

  // Function to get dashboard statistics
  getDashboardStats: async () => {
    try {
      // Make dashboard/stats publicly accessible without authentication
      const response = await fetch(`${API_BASE_URL}/dashboard/stats/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      
      // Fallback to mock data if API call fails
      return {
        automation: {
          total_runs: 120,
          successful_runs: 95,
          failed_runs: 25,
          completion_status_counts: {
            both_agents_present: 45,
            listing_agents_only: 25,
            selling_agents_only: 20,
            fsbo_no_agents: 8,
            agent_copy_missing: 12,
            both_agents_missing: 10
          }
        },
        recent_runs: [
          { id: 1, name: 'Data Import', status: 'COMPLETED', success: true, started_at: '2023-06-15T10:30:00Z' },
          { id: 2, name: 'Report Generation', status: 'COMPLETED', success: true, started_at: '2023-06-14T14:45:00Z' },
          { id: 3, name: 'Data Sync', status: 'FAILED', success: false, started_at: '2023-06-13T09:15:00Z' },
          { id: 4, name: 'Backup Process', status: 'COMPLETED', success: true, started_at: '2023-06-12T18:20:00Z' },
          { id: 5, name: 'User Import', status: 'FAILED', success: false, started_at: '2023-06-11T11:10:00Z' }
        ]
      };
    }
  },

  // Function to get filtered automation runs with pagination
  getFilteredAutomationRuns: async (completionStatus = null, page = 1, limit = 10, status = null) => {
    try {
      let url = `${API_BASE_URL}/automation/filtered-runs/?page=${page}&limit=${limit}`;
      if (completionStatus) {
        url += `&completion_status=${completionStatus}`;
      }
      if (status) {
        url += `&status=${status}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching filtered automation runs:', error);
      
      // Fallback to mock data if API call fails
      const mockData = {
        results: [
          { 
            id: 1, 
            orderNumber: 'ORD-001', 
            completionStatus: 'both_agents_present', 
            createdAt: '2023-06-15T10:30:00Z', 
            properties: [{ address1: '123 Main St', city: 'Springfield', state: 'IL', zipcode: '62701' }],
            error: null
          },
          { 
            id: 2, 
            orderNumber: 'ORD-002', 
            completionStatus: 'listing_agents_only', 
            createdAt: '2023-06-14T14:45:00Z', 
            properties: [{ address1: '456 Oak Ave', city: 'Chicago', state: 'IL', zipcode: '60601' }],
            error: null
          },
          { 
            id: 3, 
            orderNumber: 'ORD-003', 
            completionStatus: null, 
            createdAt: '2023-06-13T09:15:00Z', 
            properties: [{ address1: '789 Pine Rd', city: 'Peoria', state: 'IL', zipcode: '61601' }],
            error: 'Database connection timeout after 30 seconds'
          },
          { 
            id: 4, 
            orderNumber: 'ORD-004', 
            completionStatus: '', 
            createdAt: '2023-06-12T18:20:00Z', 
            properties: [{ address1: '321 Elm St', city: 'Rockford', state: 'IL', zipcode: '61101' }],
            error: 'API rate limit exceeded - please try again later'
          },
          { 
            id: 5, 
            orderNumber: 'ORD-005', 
            completionStatus: 'agent_copy_missing', 
            createdAt: '2023-06-11T11:10:00Z', 
            properties: [{ address1: '654 Maple Dr', city: 'Aurora', state: 'IL', zipcode: '60502' }],
            error: null
          },
          { 
            id: 6, 
            orderNumber: 'ORD-006', 
            completionStatus: null, 
            createdAt: '2023-06-10T08:30:00Z', 
            properties: [{ address1: '987 Cedar Ln', city: 'Joliet', state: 'IL', zipcode: '60431' }],
            error: 'Invalid property data format received from external service'
          }
        ].filter(item => {
          if (!completionStatus) return true;
          if (completionStatus === 'no_completion_status') {
            return !item.completionStatus || item.completionStatus === '';
          }
          return item.completionStatus === completionStatus;
        }),
        count: 50,
        num_pages: 5,
        current_page: page,
        has_next: page < 5,
        has_previous: page > 1,
        next_page: page < 5 ? page + 1 : null,
        previous_page: page > 1 ? page - 1 : null
      };
      
      return mockData;
    }
  },

  // Function to get automation runs with pagination and optional status filtering
  getAutomationRuns: async (page = 1, pageSize = 10, statusFilter = null, completionStatus = null) => {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      // Add status filter if provided
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Add completion status filter if provided
      if (completionStatus && completionStatus !== 'all') {
        params.append('completion_status', completionStatus);
      }
      
      // Make a fetch call to the backend API using the automation-run-webhook endpoint
      const response = await fetch(`${API_BASE_URL}/automation/automation-run-webhook/?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching automation runs:', error);
      
      // Fallback to mock data if API call fails
      return {
        results: [
          { id: 1, name: 'Data Import', status: 'COMPLETED', started_at: '2023-06-15T10:30:00Z', completed_at: '2023-06-15T10:35:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 2, name: 'Report Generation', status: 'COMPLETED', started_at: '2023-06-14T14:45:00Z', completed_at: '2023-06-14T14:50:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 3, name: 'Data Sync', status: 'FAILED', started_at: '2023-06-13T09:15:00Z', completed_at: '2023-06-13T09:20:00Z', success: false, error: 'Connection timeout', duration: 300, parameters: {}, result: {} },
          { id: 4, name: 'Backup Process', status: 'COMPLETED', started_at: '2023-06-12T18:20:00Z', completed_at: '2023-06-12T18:25:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 5, name: 'User Import', status: 'FAILED', started_at: '2023-06-11T11:10:00Z', completed_at: '2023-06-11T11:15:00Z', success: false, error: 'Invalid data format', duration: 300, parameters: {}, result: {} },
          { id: 6, name: 'System Check', status: 'COMPLETED', started_at: '2023-06-10T08:30:00Z', completed_at: '2023-06-10T08:35:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 7, name: 'Database Cleanup', status: 'COMPLETED', started_at: '2023-06-09T16:45:00Z', completed_at: '2023-06-09T16:50:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 8, name: 'Log Rotation', status: 'COMPLETED', started_at: '2023-06-08T22:15:00Z', completed_at: '2023-06-08T22:20:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} },
          { id: 9, name: 'Security Scan', status: 'FAILED', started_at: '2023-06-07T13:10:00Z', completed_at: '2023-06-07T13:15:00Z', success: false, error: 'Scan interrupted', duration: 300, parameters: {}, result: {} },
          { id: 10, name: 'Config Update', status: 'COMPLETED', started_at: '2023-06-06T09:30:00Z', completed_at: '2023-06-06T09:35:00Z', success: true, error: null, duration: 300, parameters: {}, result: {} }
        ],
        count: 50,
        num_pages: 5,
        current_page: page
      };
    }
  },

  // Function to setup WebSocket connection for real-time updates
  setupAutomationRunsWebSocket: (onUpdate) => {
    try {
      // Connect to the WebSocket
      const socket = new WebSocket(`${WS_BASE_URL}/ws/automation/runs/`);
      
      socket.onopen = () => {
        console.log('WebSocket connection established');
      };
      
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log({data})
          if (data.type === 'automation_run_update') {
            onUpdate(data.automation_run);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      socket.onclose = (event) => {
        console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
      };
      
      // Return a cleanup function
      return () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      };
    } catch (error) {
      console.error('Error setting up WebSocket:', error);
      
      // Fallback to mock updates if WebSocket setup fails
      const interval = setInterval(() => {
        const mockUpdate = {
          id: Math.floor(Math.random() * 1000) + 100,
          name: `Automated Task ${Math.floor(Math.random() * 100)}`,
          status: Math.random() > 0.3 ? 'COMPLETED' : 'FAILED',
          started_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          success: Math.random() > 0.3,
          error: Math.random() > 0.3 ? null : 'Random error occurred',
          duration: Math.floor(Math.random() * 600),
          parameters: {},
          result: {}
        };
        
        onUpdate(mockUpdate);
      }, 10000);
      
      return () => clearInterval(interval);
    }
  },

  // Function to get a specific automation run by ID
  getAutomationRun: async (automationRunId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/automation/runs/${automationRunId}/`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching automation run:', error);
      
      // Fallback to mock data if API call fails
      return {
        id: automationRunId,
        name: `Automation Run ${automationRunId}`,
        status: 'COMPLETED',
        started_at: '2023-06-15T10:30:00Z',
        completed_at: '2023-06-15T10:35:00Z',
        success: true,
        error: null,
        duration: 300,
        parameters: {},
        result: {}
      };
    }
  },

  // Function to get logs for a specific automation run
  getAutomationRunLogs: async (automationRunId, page = 1, pageSize = 50) => {
    try {
      const response = await fetch(`${API_BASE_URL}/automation/logs/${automationRunId}/?page=${page}&page_size=${pageSize}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching automation run logs:', error);
      
      // Fallback to mock data if API call fails
      return {
        results: [
          {
            id: 1,
            timestamp: '2023-06-15T10:30:00Z',
            level: 'INFO',
            message: 'Automation run started',
            module: 'automation_service',
            automation_run_id: automationRunId,
            additional_data: null
          },
          {
            id: 2,
            timestamp: '2023-06-15T10:31:00Z',
            level: 'INFO',
            message: 'Processing data...',
            module: 'data_processor',
            automation_run_id: automationRunId,
            additional_data: { records_processed: 100 }
          },
          {
            id: 3,
            timestamp: '2023-06-15T10:32:00Z',
            level: 'WARNING',
            message: 'Some records were skipped due to validation errors',
            module: 'data_validator',
            automation_run_id: automationRunId,
            additional_data: { skipped_records: 5 }
          },
          {
            id: 4,
            timestamp: '2023-06-15T10:35:00Z',
            level: 'INFO',
            message: 'Automation run completed successfully',
            module: 'automation_service',
            automation_run_id: automationRunId,
            additional_data: { total_processed: 95, total_skipped: 5 }
          }
        ],
        count: 4,
        num_pages: 1,
        current_page: page
      };
    }
  },

  // Function to get unread notifications count
  getUnreadNotificationsCount: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/notifications/unread-count/`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching unread notifications count:', error);
      return { unread_count: 0 };
    }
  },

  // Function to get notifications list
  getNotifications: async (page = 1, pageSize = 30) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/notifications/?page=${page}&page_size=${pageSize}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { results: [], count: 0 };
    }
  },

  // Function to mark all notifications as read
  markAllNotificationsAsRead: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/notifications/mark-all-read/`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return null;
    }
  },

  // Function to mark specific notification as read
  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/notifications/${notificationId}/mark-read/`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return null;
    }
  },

  // Function to delete all read notifications
  deleteReadNotifications: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/notifications/delete-read/`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      return null;
    }
  },

  // Function to setup WebSocket connection for real-time dashboard updates
  setupDashboardWebSocket: (onUpdate) => {
    try {
      // Connect to the WebSocket
      const socket = new WebSocket(`${WS_BASE_URL}/ws/dashboard/`);
      
      socket.onopen = () => {
        console.log('Dashboard WebSocket connection established');
      };
      
      socket.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Dashboard WebSocket data:', data);
          if (data.type === 'dashboard_update') {
            onUpdate(data.data);
          }
          
          // Call unread notifications API when socket data is received
          try {
            const notificationData = await apiService.getUnreadNotificationsCount();
            // Dispatch custom event for notification count update
            window.dispatchEvent(new CustomEvent('notificationCountUpdate', {
              detail: notificationData
            }));
          } catch (notificationError) {
            console.error('Error fetching notification count:', notificationError);
          }
        } catch (error) {
          console.error('Error parsing dashboard WebSocket message:', error);
        }
      };
      
      socket.onerror = (error) => {
        console.error('Dashboard WebSocket error:', error);
      };
      
      socket.onclose = (event) => {
        console.log(`Dashboard WebSocket connection closed: ${event.code} ${event.reason}`);
      };
      
      // Return a cleanup function
      return () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      };
    } catch (error) {
      console.error('Error setting up dashboard WebSocket:', error);
      
      // Fallback to mock updates if WebSocket setup fails
      const interval = setInterval(() => {
        const mockStats = {
          stats: {
            total_runs: 0,
            successful_runs: 0,
            failed_runs: 0,
            completion_status_counts: {
              both_agents_present: 0,
              listing_agents_only: 0,
              selling_agents_only: 0,
              fsbo_no_agents: 0,
              agent_copy_missing: 0,
              both_agents_missing: 0
            }
          },
          recent_runs: [
            
          ]
        };
        
        onUpdate(mockStats);
      }, 15000);
      
      return () => clearInterval(interval);
    }
  },

  // Get all salespersons with pagination and search
  getSalesPersons: async (page = 1, pageSize = 10, searchId = '') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (searchId && searchId.trim()) {
        params.append('search', searchId.trim());
      }
      
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/salespersons/?${params.toString()}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching salespersons:', error);
      throw error;
    }
  },

  // Get commissions for a specific salesperson
  getSalesPersonCommissions: async (salespersonId, page = 1, pageSize = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        // Include limit to align with backend pagination param
        limit: pageSize.toString()
      });
      
      // Add date filters
      if (filters.dateFilter === 'month' && filters.month) {
        params.append('month', filters.month);
      } else if (filters.dateFilter === 'date_range' && filters.startDate && filters.endDate) {
        params.append('start_date', filters.startDate);
        params.append('end_date', filters.endDate);
      }
      // For current_month or no filter, the backend will default to current month
      
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/salespersons/${salespersonId}/commissions/?${params.toString()}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching salesperson commissions:', error);
      throw error;
    }
  },

  // Get file commissions list with optional date filters (month or date range)
  getFileCommissions: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        // Include limit to align with backend pagination param
        limit: pageSize.toString()
      });

      if (filters.dateFilter === 'month' && filters.month) {
        params.append('month', filters.month);
      } else if (filters.dateFilter === 'date_range' && filters.startDate && filters.endDate) {
        params.append('start_date', filters.startDate);
        params.append('end_date', filters.endDate);
      }
      // For current_month or no filter, the backend will default to current month

      // New name-based filters
      if (filters.salesPersonName) {
        params.append('sales_person_name', filters.salesPersonName);
      }
      if (filters.transactionCoordinator) {
        params.append('transaction_coordinator', filters.transactionCoordinator);
      }
      if (filters.processor) {
        params.append('processor', filters.processor);
      }

      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/commissions/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching file commissions:', error);
      throw error;
    }
  },

  // Management: SalesPersons CRUD
  getSalesPersonsManage: async () => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/salespersons/manage/`, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  createSalesPerson: async (payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/salespersons/manage/`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  updateSalesPerson: async (id, payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/salespersons/manage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  deleteSalesPerson: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/salespersons/manage/${id}/`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return true;
  },

  // Management: Managers CRUD
  getManagersManage: async () => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/managers/manage/`, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  createManager: async (payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/managers/manage/`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  updateManager: async (id, payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/managers/manage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  deleteManager: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/managers/manage/${id}/`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return true;
  },

  // Management: Transaction Coordinators CRUD
  getTransactionCoordinatorsManage: async () => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/transaction-coordinators/manage/`, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  createTransactionCoordinator: async (payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/transaction-coordinators/manage/`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  updateTransactionCoordinator: async (id, payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/transaction-coordinators/manage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  deleteTransactionCoordinator: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/transaction-coordinators/manage/${id}/`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return true;
  },

  // Management: Processors CRUD
  getProcessorsManage: async () => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/processors/manage/`, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  createProcessor: async (payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/processors/manage/`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  updateProcessor: async (id, payload) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/processors/manage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  },
  deleteProcessor: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/processors/manage/${id}/`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return true;
  },

  // Delete a specific automation run
  deleteAutomationRun: async (automationRunId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/runs/${automationRunId}/`,
        {
          method: 'DELETE'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return { success: true, message: 'Automation run deleted successfully' };
    } catch (error) {
      console.error('Error deleting automation run:', error);
      throw error;
    }
  },

  // Delete logs for a specific automation run
  deleteAutomationLogs: async (automationRunId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/runs/${automationRunId}/logs/`,
        {
          method: 'DELETE'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return { success: true, message: 'Automation logs deleted successfully' };
    } catch (error) {
      console.error('Error deleting automation logs:', error);
      throw error;
    }
  },

  // Delete all automation runs and their associated logs
  deleteAllAutomationData: async () => {
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/runs/delete-all/`,
        {
          method: 'DELETE'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return { success: true, message: 'All automation data deleted successfully' };
    } catch (error) {
      console.error('Error deleting all automation data:', error);
      throw error;
    }
  }
};

// Export helper functions and apiService for use in other components
export { apiService, getAccessToken, getRefreshToken, setTokens, clearTokens, makeAuthenticatedRequest };

export default apiService;